import { useCallback, useRef, useState } from "react";
import { authApi } from "../../api";
import { MerchantAccountFormData, MerchantBusinessFormData } from "../types/merchantRegister.types";
import { useResultHandler } from "../../../../hooks/useResultHandler";
import { ResultCode } from "../../../../lib/result-codes";
import { useModal } from "../../../../components";
import { useNavigate } from "react-router-dom";
import { LoginResponseData } from "../../data";
import { useAuthSession } from "../../shared/hooks/auth-session-context";
import { MERCHANT_ONBOARDING_STAGES } from "../../shared";

export const useRegisterMerchantAccount = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { addModal } = useModal();
  const { handleResult } = useResultHandler();
  const navigate = useNavigate();
  const { setSession } = useAuthSession();

  const execute = useCallback(
    async (data: MerchantAccountFormData): Promise<LoginResponseData | undefined> => {
      if (isLoading) return undefined;
      setIsLoading(true);
      try {
        const response = await authApi.registerMerchantAccount(data);
        const result = response.data;
        handleResult(response, {
          edgeCases: {
            [ResultCode.AuthEmailAlreadyExists]: () => {
              addModal({
                type: "warning",
                title: "Account already exists",
                message: "An account with this email has already been registered.",
                cancelLabel: "Cancel",
                actionLabel: "Sign in",
                onConfirm: () => navigate("/login"),
              });
            },
          },
        });

        if (
          response.code === ResultCode.Success &&
          result?.user &&
          result.access_token &&
          result.refresh_token
        ) {
          setSession({
            user: result.user,
            token: result.access_token,
            refreshToken: result.refresh_token,
            merchantOnboardingStage: MERCHANT_ONBOARDING_STAGES.verifyEmail,
          });
        }
        return response.code === ResultCode.Success ? (result ?? undefined) : undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [addModal, handleResult, isLoading, navigate, setSession],
  );

  return { execute, isLoading };
};

export const useVerifyMerchantEmail = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { handleResult } = useResultHandler();
  const execute = useCallback(
    async (email: string, code: string) => {
      if (isLoading) return undefined;
      setIsLoading(true);
      try {
        const result = await authApi.verifyMerchantEmail(email, code);
        handleResult(result);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [handleResult, isLoading],
  );
  return { execute, isLoading };
};

export const useCreateMerchantBusinessProfile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { handleResult } = useResultHandler();
  const execute = useCallback(
    async (data: MerchantBusinessFormData) => {
      if (isLoading) return undefined;
      setIsLoading(true);
      try {
        const response = await authApi.createMerchantBusinessProfile(data);
        handleResult(response);
        return response.success ? (response.data ?? undefined) : undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [handleResult, isLoading],
  );
  return { execute, isLoading };
};

export const useSendMerchantVerificationCode = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const execute = useCallback(async (email: string): Promise<number> => {
    if (isLoadingRef.current) return 0;

    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const result = await authApi.sendMerchantVerificationCode(email);
      return result.data?.remaining_seconds ?? 0;
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);
  return { execute, isLoading };
};
